import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { MaterialModule } from 'src/app/material.module';
import { EDF_GUIDE_URL } from 'src/app/pages/dashboard/rbi-edf-notice-banner/rbi-edf-notice';
import { AuthService } from 'src/app/services/auth.service';
import { CoreService } from 'src/app/services/core.service';
import { IProfileManagementService } from '../../../../services/business/IProfileManagementService';
import { SharedProfileData } from '../../../../services/data/shared-profile-data';
import { isAdminUser } from '../../../../shared/utils/admin.util';
import { Profile } from '../../../../types/Profile';
import { UserProfile } from '../../../../types/UserProfile';
import { navItems } from '../sidebar/sidebar-data';
interface notifications {
  id: number;
  icon: string;
  color: string;
  title: string;
  time: string;
  subtitle: string;
}

interface inbox {
  id: number;
  bgcolor: string;
  imagePath: string;
  title: string;
  time: string;
  subtitle: string;
}

interface profiledd {
  id: number;
  title: string;
  link: string;
  new?: boolean;
}

interface apps {
  id: number;
  icon: string;
  color: string;
  title: string;
  subtitle: string;
  link: string;
}

interface quicklinks {
  id: number;
  title: string;
  link: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    NgScrollbarModule,
    TablerIconsModule,
    MaterialModule,
    NgxSkeletonLoaderModule,
  ],
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnDestroy {
  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
  showFiller = false;
  isLoadingCompanyList: boolean = true;
  isLoadingSelectedCompany: boolean = false;
  rateCardExpanded: boolean = false;
  isLoadingExchangeRate: boolean = true;

  async ngOnInit(): Promise<void> {
    this.sellCurrencySubscription = this._sharedProfileData
      .getSelectedSellCurrency$()
      .pipe(
        filter((code) => !!code),
        distinctUntilChanged(),
      )
      .subscribe((code) => {
        this.sellCurrency = code;
        void this.fetchExchangeRates();
      });

    await this.checkAffiliateManagerStatus();
    await this.fetchCompanyList();
    await this.initializeSellCurrency();
    console.log('This is header component');

    // Subscribe to profile changes to update the header when profile is updated
    this.profileSubscription = this._sharedProfileData
      .getUserProfile$()
      .subscribe((profile) => {
        if (
          profile &&
          this.selectedCompany &&
          profile.businessId === this.selectedCompany.businessId
        ) {
          // Update the selected company with the new profile data
          this.selectedCompany = { ...profile };
          this.getSelectedCompanyLogo(this.selectedCompany);
        }
      });
  }

  async fetchExchangeRates(): Promise<void> {
    this.isLoadingExchangeRate = true;
    try {
      const response = await this._profileService.getConversionRate(
        this.sellCurrency,
        this.buyCurrency,
      );
      this.userConversionRate = response.userConversionRate;
      this.midMarketRate = response.midMarketRate;
      console.log('Exchange rates:', {
        userConversionRate: this.userConversionRate,
        midMarketRate: this.midMarketRate,
      });
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
    } finally {
      this.isLoadingExchangeRate = false;
    }
  }

  private async initializeSellCurrency(): Promise<void> {
    if (this._sharedProfileData.getSelectedSellCurrency()) {
      return;
    }

    const profile =
      this.selectedCompany ??
      (await this._profileService.getProfile(false).catch(() => null));
    const defaultCode = profile?.vbanCurrencyOptions?.[0]?.code ?? 'USD';
    this._sharedProfileData.setSelectedSellCurrency(defaultCode);
  }

  async checkAffiliateManagerStatus(): Promise<void> {
    try {
      this.userProfile = await this._profileService.getUserProfile();
      this.isAffiliateManager = this.userProfile?.affiliateManager === true;
      const cognitoUser = await this.authService.getCurrentAuthenticatedUser();
      this.isPlatformAdmin = isAdminUser(this.userProfile, cognitoUser);
    } catch (error) {
      console.error('Error checking affiliate manager status:', error);
      this.isAffiliateManager = false;
      this.isPlatformAdmin = false;
    }
  }

  async fetchCompanyList(): Promise<void> {
    this.isLoadingCompanyList = true;
    try {
      if (this.isPlatformAdmin) {
        this.companyList = [];
        this.isLoadingCompanyList = false;
        return;
      }

      this.companyList = await this._profileService.getAllActiveProfiles();
      if (this.companyList.length == 0 && !this.isAffiliateManager) {
        this.router.navigate(['/kyc-onboarding']);
      }
      try {
        this.selectedCompany = await this._profileService.getProfile();
        this.getSelectedCompanyLogo(this.selectedCompany);
        if (this.selectedCompany.name == '' && this.companyList.length > 0) {
          await this.onSelectItem(this.companyList[0]);
        }
      } catch (error) {
        // If affiliate manager doesn't have a profile, that's okay
        if (!this.isAffiliateManager) {
          console.error('Error getting profile:', error);
        }
      }
      if (
        Array.isArray(this.companyList) ||
        this.selectedCompany ||
        this.isAffiliateManager
      ) {
        this.isLoadingCompanyList = false;
      }
    } catch (error) {
      this._coreService.onFailureToaster(
        'Error fetching your organizations',
        error,
      );
      this.isLoadingCompanyList = false;
    }
  }

  getSelectedCompanyLogo(selectedItem: Profile): string {
    // Clean up previous blob URL if it exists
    if (
      this.selectedCompanyLogo &&
      this.selectedCompanyLogo.startsWith('blob:')
    ) {
      URL.revokeObjectURL(this.selectedCompanyLogo);
    }

    if (selectedItem.profileImage) {
      this.selectedCompanyLogo = URL.createObjectURL(selectedItem.profileImage);
    } else if (selectedItem.profilePicture) {
      this.selectedCompanyLogo = selectedItem.profilePicture;
    } else {
      this.selectedCompanyLogo = '/assets/images/profile/placeholder.png';
    }
    return this.selectedCompanyLogo;
  }

  public selectedLanguage: any = {
    language: 'English',
    code: 'en',
    type: 'US',
    icon: '/assets/images/flag/icon-flag-en.svg',
  };

  public languages: any[] = [
    {
      language: 'English',
      code: 'en',
      type: 'US',
      icon: '/assets/images/flag/icon-flag-en.svg',
    },
    {
      language: 'Español',
      code: 'es',
      icon: '/assets/images/flag/icon-flag-es.svg',
    },
    {
      language: 'Français',
      code: 'fr',
      icon: '/assets/images/flag/icon-flag-fr.svg',
    },
    {
      language: 'German',
      code: 'de',
      icon: '/assets/images/flag/icon-flag-de.svg',
    },
  ];

  constructor(
    private _profileService: IProfileManagementService,
    private _coreService: CoreService,
    private _sharedProfileData: SharedProfileData,
    public dialog: MatDialog,
    private translate: TranslateService,
    private router: Router,
    private authService: AuthService,
  ) {
    translate.setDefaultLang('en');
  }

  options = this._coreService.getOptions();

  setDark() {
    this._coreService.toggleTheme();
  }

  toggleRateCard() {
    this.rateCardExpanded = !this.rateCardExpanded;
  }

  openDialog() {
    const dialogRef = this.dialog.open(AppSearchDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
      console.log(`Dialog result: ${result}`);
    });
  }

  changeLanguage(lang: any): void {
    this.translate.use(lang.code);
    this.selectedLanguage = lang;
  }

  notifications: notifications[] = [
    {
      id: 1,
      icon: 'a-b-2',
      color: 'primary',
      time: '8:30 AM',
      title: 'Launch Admin',
      subtitle: 'Just see the my new admin!',
    },
    {
      id: 2,
      icon: 'calendar',
      color: 'accent',
      time: '8:21 AM',
      title: 'Event today',
      subtitle: 'Just a reminder that you have event',
    },
    {
      id: 3,
      icon: 'settings',
      color: 'warning',
      time: '8:05 AM',
      title: 'Settings',
      subtitle: 'You can customize this template',
    },
    {
      id: 4,
      icon: 'a-b-2',
      color: 'success',
      time: '7:30 AM',
      title: 'Launch Templates',
      subtitle: 'Just see the my new admin!',
    },
    {
      id: 5,
      icon: 'exclamation-circle',
      color: 'error',
      time: '7:03 AM',
      title: 'Event tomorrow',
      subtitle: 'Just a reminder that you have event',
    },
  ];

  inbox: inbox[] = [
    {
      id: 1,
      bgcolor: 'bg-error',
      imagePath: 'assets/images/profile/user-6.jpg',
      time: 'just now',
      title: 'Michell Flintoff',
      subtitle: 'You: Yesterdy was great...',
    },
    {
      id: 2,
      bgcolor: 'bg-primary',
      imagePath: 'assets/images/profile/user-2.jpg',
      time: '5 mins ago',
      title: 'Bianca Anderson',
      subtitle: 'Nice looking dress you...',
    },
    {
      id: 3,
      bgcolor: 'bg-success',
      imagePath: 'assets/images/profile/user-3.jpg',
      time: '10 mins ago',
      title: 'Andrew Johnson',
      subtitle: 'Sent a photo',
    },
    {
      id: 4,
      bgcolor: 'bg-warning',
      imagePath: 'assets/images/profile/user-4.jpg',
      time: 'days ago',
      title: 'Marry Strokes',
      subtitle: "If I don't like something",
    },
    {
      id: 5,
      bgcolor: 'bg-success',
      imagePath: 'assets/images/profile/user-5.jpg',
      time: 'year ago',
      title: 'Josh Anderson',
      subtitle: '$230 deducted from account',
    },
  ];

  profiledd: profiledd[] = [
    {
      id: 1,
      title: 'My Profile',
      link: '/',
    },
    {
      id: 2,
      title: 'My Subscription',
      link: '/',
    },
    {
      id: 3,
      title: 'My Invoice',
      new: true,
      link: '/',
    },
    {
      id: 4,
      title: ' Account Settings',
      link: '/',
    },
    {
      id: 5,
      title: 'Sign Out',
      link: '/authentication/login',
    },
  ];

  apps: apps[] = [
    {
      id: 1,
      icon: 'solar:chat-line-line-duotone',
      color: 'primary',
      title: 'Chat Application',
      subtitle: 'Messages & Emails',
      link: '/apps/chat',
    },
    {
      id: 2,
      icon: 'solar:checklist-minimalistic-line-duotone',
      color: 'accent',
      title: 'Todo App',
      subtitle: 'Completed task',
      link: '/apps/todo',
    },
    {
      id: 3,
      icon: 'solar:bill-list-line-duotone',
      color: 'success',
      title: 'Invoice App',
      subtitle: 'Get latest invoice',
      link: '/invoices',
    },
    {
      id: 4,
      icon: 'solar:calendar-line-duotone',
      color: 'error',
      title: 'Calendar App',
      subtitle: 'Get Dates',
      link: '/apps/calendar',
    },
    {
      id: 5,
      icon: 'solar:smartphone-2-line-duotone',
      color: 'warning',
      title: 'Contact Application',
      subtitle: '2 Unsaved Contacts',
      link: '/apps/contacts',
    },
    {
      id: 6,
      icon: 'solar:ticket-line-duotone',
      color: 'primary',
      title: 'Tickets App',
      subtitle: 'Create new ticket',
      link: '/apps/tickets',
    },
    {
      id: 7,
      icon: 'solar:letter-line-duotone',
      color: 'accent',
      title: 'Email App',
      subtitle: 'Get new emails',
      link: '/apps/email/inbox',
    },
    {
      id: 8,
      icon: 'solar:book-2-line-duotone',
      color: 'warning',
      title: 'Courses',
      subtitle: 'Create new course',
      link: '/apps/courses',
    },
  ];
  quicklinks: quicklinks[] = [
    {
      id: 1,
      title: 'Pricing Page',
      link: '/theme-pages/pricing',
    },
    {
      id: 2,
      title: 'Authentication Design',
      link: '/authentication/login',
    },
    {
      id: 3,
      title: 'Register Now',
      link: '/authentication/side-register',
    },
    {
      id: 4,
      title: '404 Error Page',
      link: '/authentication/error',
    },
    {
      id: 5,
      title: 'Notes App',
      link: '/apps/notes',
    },
    {
      id: 6,
      title: 'Employee App',
      link: '/apps/employee',
    },
    {
      id: 7,
      title: 'Todo Application',
      link: '/apps/todo',
    },
  ];
  companyList: Profile[] = [];
  selectedCompany: Profile;
  selectedCompanyLogo: string = '';
  private logoCache: Map<string, string> = new Map();
  private profileSubscription: Subscription;
  private sellCurrencySubscription: Subscription;
  userProfile: UserProfile | null = null;
  isAffiliateManager: boolean = false;
  isPlatformAdmin: boolean = false;
  readonly edfGuideUrl = EDF_GUIDE_URL;
  userConversionRate: number = 0;
  midMarketRate: number = 0;
  sellCurrency = 'USD';
  buyCurrency = 'INR';
  conversionRateValidUpTo: string = '';

  async onSelectItem(item: Profile): Promise<void> {
    this.isLoadingSelectedCompany = true;
    this.selectedCompany = item;
    this.getSelectedCompanyLogo(this.selectedCompany);
    console.log(`Selected item: ${item.name}`);
    try {
      let profile = await this._profileService.setProfile(item);
      if (this.menuTrigger) {
        this.menuTrigger.closeMenu();
      }
      if (profile && profile.name != undefined && profile.name != '') {
        window.location.reload();
      } else if (!this.isPlatformAdmin) {
        this.router.navigate(['/kyc-onboarding']);
      }
    } catch (error) {
      this._coreService.onFailureToaster(
        'Error setting your selected organization as the default',
        error,
      );
    } finally {
      this.isLoadingSelectedCompany = false;
    }
  }

  onSelectedCompanyItemLogo(selectedCompanyItem: Profile): string {
    // Avoid creating blob URLs during change detection cycles
    // Use cached URLs or static fallback
    if (selectedCompanyItem.profileImage) {
      // Only create blob URL if we don't already have one for this profile
      const cacheKey = selectedCompanyItem.businessId + '_logo';
      if (!this.logoCache) {
        this.logoCache = new Map();
      }

      let cachedUrl = this.logoCache.get(cacheKey);
      if (!cachedUrl) {
        cachedUrl = URL.createObjectURL(selectedCompanyItem.profileImage);
        this.logoCache.set(cacheKey, cachedUrl);
      }
      return cachedUrl;
    } else if (selectedCompanyItem.profilePicture) {
      return selectedCompanyItem.profilePicture;
    } else {
      return '/assets/images/profile/placeholder.png';
    }
  }

  isSelectedCompany(item: Profile): boolean {
    return (
      this.selectedCompany &&
      this.selectedCompany.name == item.name &&
      this.selectedCompany.businessId == item.businessId
    );
  }

  async signOut(): Promise<void> {
    await this.authService.signOutUser();
  }

  ngOnDestroy(): void {
    // Unsubscribe from profile changes
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
    }
    if (this.sellCurrencySubscription) {
      this.sellCurrencySubscription.unsubscribe();
    }

    // Clean up all cached blob URLs to prevent memory leaks
    if (this.logoCache) {
      this.logoCache.forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      this.logoCache.clear();
    }

    // Clean up selected company logo
    if (
      this.selectedCompanyLogo &&
      this.selectedCompanyLogo.startsWith('blob:')
    ) {
      URL.revokeObjectURL(this.selectedCompanyLogo);
    }
  }
}

@Component({
  selector: 'search-dialog',
  standalone: true,
  imports: [RouterModule, MaterialModule, TablerIconsModule, FormsModule],
  templateUrl: 'search-dialog.component.html',
})
export class AppSearchDialogComponent {
  searchText: string = '';
  navItems = navItems;

  navItemsData = navItems.filter((navitem) => navitem.displayName);
}
